// Nur als Typ: pdf-unicode haengt sich in ein FERTIGES Dokument ein, es baut
// keines. Ein Laufzeit-Import zoege jsPDF wieder ins Startbuendel.
import type { jsPDF } from 'jspdf';

/**
 * Sonderzeichen im PDF-Export.
 *
 * Die PDF-Standardschriften (Helvetica & Co.) kennen nur den Windows-Zeichensatz
 * (cp1252). Trifft jsPDF beim Zeichnen auf ein Zeichen ausserhalb davon — z.B. den
 * Wechselpfeil in Horizont-Symbolen wie "Bb↔C" — schreibt es den KOMPLETTEN Text
 * als UTF-16-Bytes in die Seite. Der PDF-Betrachter liest diese Bytes als cp1252
 * und zeigt Zeichensalat ("Bb !Ô C") — nicht nur an der Stelle des Sonderzeichens,
 * sondern in der ganzen Zelle.
 *
 * Loesung ohne eingebettete Schriftdatei: Pfeile, Vergleichszeichen und griechische
 * Buchstaben liefert die PDF-Standardschrift "Symbol". installPdfUnicodeText() haengt
 * sich in doc.text() ein, zeichnet die betroffenen Zeichen in Symbol und den Rest wie
 * bisher. Zeichen, die auch Symbol nicht kennt, werden zu "?" — statt die Zeile zu
 * zerschiessen.
 */

/**
 * Ein Sonderzeichen in der PDF-Standardschrift "Symbol".
 * `breite` = Vorschub des Zeichens in Schrifthoehen (em) laut Symbol-Schriftmetrik.
 * jsPDF kennt diese Metrik nicht (es rechnet pauschal mit 0,58 em) — ohne die
 * Tabelle liefe der Text ins Zeichen hinein ("C↔Bb" ueberlappt).
 */
interface SymbolChar {
	code: number;
	breite: number;
}

/** Unicode-Zeichen -> Zeichencode + Breite in der PDF-Standardschrift "Symbol". */
export const SYMBOL_CHARS: Readonly<Record<string, SymbolChar>> = {
	// Pfeile (einfach / doppelt)
	'↔': { code: 0xab, breite: 1.042 }, '←': { code: 0xac, breite: 0.987 }, '↑': { code: 0xad, breite: 0.603 },
	'→': { code: 0xae, breite: 0.987 }, '↓': { code: 0xaf, breite: 0.603 },
	'⇔': { code: 0xdb, breite: 1.042 }, '⇐': { code: 0xdc, breite: 0.987 }, '⇑': { code: 0xdd, breite: 0.603 },
	'⇒': { code: 0xde, breite: 0.987 }, '⇓': { code: 0xdf, breite: 0.603 },
	// Mathematische Zeichen (nur solche, die cp1252 NICHT kennt — ±, ×, ÷, · kann Helvetica selbst)
	'≤': { code: 0xa3, breite: 0.549 }, '≥': { code: 0xb3, breite: 0.549 }, '≠': { code: 0xb9, breite: 0.549 },
	'≈': { code: 0xbb, breite: 0.549 }, '∅': { code: 0xc6, breite: 0.823 }, '∞': { code: 0xa5, breite: 0.713 },
	'√': { code: 0xd6, breite: 0.549 }, '∑': { code: 0xe5, breite: 0.713 }, '∙': { code: 0xd7, breite: 0.25 },
	// Griechische Buchstaben (z.B. Substrat-/Horizontzeichen der Bodensteckbriefe)
	'α': { code: 0x61, breite: 0.631 }, 'β': { code: 0x62, breite: 0.549 }, 'γ': { code: 0x67, breite: 0.411 },
	'δ': { code: 0x64, breite: 0.494 }, 'ε': { code: 0x65, breite: 0.439 }, 'ζ': { code: 0x7a, breite: 0.494 },
	'η': { code: 0x68, breite: 0.603 }, 'θ': { code: 0x71, breite: 0.521 }, 'ι': { code: 0x69, breite: 0.329 },
	'κ': { code: 0x6b, breite: 0.549 }, 'λ': { code: 0x6c, breite: 0.549 }, 'μ': { code: 0x6d, breite: 0.576 },
	'ν': { code: 0x6e, breite: 0.521 }, 'ξ': { code: 0x78, breite: 0.493 }, 'ο': { code: 0x6f, breite: 0.549 },
	'π': { code: 0x70, breite: 0.549 }, 'ρ': { code: 0x72, breite: 0.549 }, 'σ': { code: 0x73, breite: 0.603 },
	'τ': { code: 0x74, breite: 0.439 }, 'υ': { code: 0x75, breite: 0.576 }, 'φ': { code: 0x66, breite: 0.521 },
	'χ': { code: 0x63, breite: 0.549 }, 'ψ': { code: 0x79, breite: 0.686 }, 'ω': { code: 0x77, breite: 0.686 },
	'Γ': { code: 0x47, breite: 0.603 }, 'Δ': { code: 0x44, breite: 0.612 }, 'Θ': { code: 0x51, breite: 0.741 },
	'Λ': { code: 0x4c, breite: 0.686 }, 'Ξ': { code: 0x58, breite: 0.645 }, 'Π': { code: 0x50, breite: 0.768 },
	'Σ': { code: 0x53, breite: 0.592 }, 'Φ': { code: 0x46, breite: 0.763 }, 'Ψ': { code: 0x59, breite: 0.795 },
	'Ω': { code: 0x57, breite: 0.768 },
};

/**
 * Sonderzeichen etwas kleiner setzen als den Text: Pfeile und griechische Zeichen
 * wirken sonst zu wuchtig neben den Buchstaben.
 */
export const SYMBOL_GROESSE = 0.85;

/** Kleiner Abstand rechts neben einem Sonderzeichen (in Schrifthoehen). */
export const SYMBOL_ABSTAND = 0.1;

/**
 * Zeichen mit Codepoint >= 0x100, die die Standardschriften trotzdem darstellen
 * koennen (cp1252 belegt den Bereich 0x80-0x9F mit diesen Unicode-Zeichen).
 */
const WINANSI_HIGH = new Set('€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ'.split(''));

/** Kann die PDF-Standardschrift dieses Zeichen darstellen? */
function isWinAnsi(ch: string): boolean {
	return (ch.codePointAt(0) ?? 0) < 0x100 || WINANSI_HIGH.has(ch);
}

/** Enthaelt der Text Zeichen, an denen die PDF-Standardschrift scheitert? */
export function hasSonderzeichen(text: string): boolean {
	return [...text].some(ch => !isWinAnsi(ch));
}

/** Textstueck, das am Stueck in einer Schrift gezeichnet wird. */
export interface PdfTextRun {
	/** Bereits in Schrift-Zeichencodes uebersetzt (bei Symbol-Stuecken). */
	text: string;
	/** true = in der Schrift "Symbol" zeichnen. */
	symbol: boolean;
}

/**
 * Zerlegt einen Text in Stuecke fuer die Normal- und die Symbol-Schrift.
 * Unbekannte Sonderzeichen werden zu "?".
 */
export function splitPdfTextRuns(text: string): PdfTextRun[] {
	const runs: PdfTextRun[] = [];
	for (const ch of text) {
		const sym = SYMBOL_CHARS[ch];
		const symbol = sym !== undefined;
		const piece = symbol ? String.fromCharCode(sym.code) : (isWinAnsi(ch) ? ch : '?');
		const last = runs[runs.length - 1];
		if (last && last.symbol === symbol) last.text += piece;
		else runs.push({ text: piece, symbol });
	}
	return runs;
}

/** Breite eines Symbol-Stuecks in em (inkl. Abstand nach rechts) — aus der Symbol-Schriftmetrik. */
function symbolBreiteEm(run: PdfTextRun): number {
	const zeichen = Object.values(SYMBOL_CHARS);
	let em = 0;
	for (const c of run.text) {
		const treffer = zeichen.find(z => z.code === c.charCodeAt(0));
		em += (treffer?.breite ?? 0.6) * SYMBOL_GROESSE + SYMBOL_ABSTAND;
	}
	return em;
}

/**
 * Macht doc.text() sonderzeichenfest. Einmal direkt nach `new jsPDF(...)` aufrufen —
 * alle spaeteren doc.text()-Aufrufe sind damit abgedeckt. Texte ohne Sonderzeichen
 * gehen unveraendert den bisherigen Weg.
 */
export function installPdfUnicodeText(doc: jsPDF): void {
	const original = doc.text.bind(doc);

	/** Eine Zeile stueckweise zeichnen (Ausrichtung ueber die Gesamtbreite). */
	const drawLine = (line: string, x: number, y: number, options?: any): void => {
		const runs = splitPdfTextRuns(line);
		const aktiv = doc.getFont();
		const groesse = doc.getFontSize();
		const skala = doc.internal.scaleFactor;   // Schriftgroesse (pt) -> Dokument-Einheit (mm)

		const useSymbol = (on: boolean) => {
			doc.setFont(on ? 'symbol' : aktiv.fontName, on ? 'normal' : aktiv.fontStyle);
			doc.setFontSize(on ? groesse * SYMBOL_GROESSE : groesse);
		};

		// Symbol-Stuecke nach Schriftmetrik messen — jsPDF misst sie falsch (zu schmal).
		const breiten = runs.map(run => run.symbol
			? symbolBreiteEm(run) * groesse / skala
			: doc.getTextWidth(run.text));
		const gesamt = breiten.reduce((a, b) => a + b, 0);

		const align = options?.align;
		let cx = align === 'center' ? x - gesamt / 2 : align === 'right' ? x - gesamt : x;

		// Ausrichtung/Umbruch haben wir hier schon aufgeloest — Stuecke linksbuendig setzen.
		const runOptions = { ...(options ?? {}) };
		delete runOptions.align;
		delete runOptions.maxWidth;

		runs.forEach((run, i) => {
			if (run.symbol) useSymbol(true);
			original(run.text, cx, y, runOptions);
			if (run.symbol) useSymbol(false);
			cx += breiten[i];
		});
	};

	(doc as any).text = (text: any, x: number, y: number, options?: any) => {
		const zeilen: string[] = typeof text === 'string' ? [text] : (Array.isArray(text) ? text : []);
		if (!zeilen.some(z => typeof z === 'string' && hasSonderzeichen(z))) {
			return original(text, x, y, options);
		}
		// Zeilenabstand wie bei jsPDF selbst (dort via TL/T*, hier in Dokument-Einheiten).
		const zeilenhoehe = doc.getLineHeight() / doc.internal.scaleFactor;
		zeilen.forEach((zeile, i) => drawLine(String(zeile), x, y + i * zeilenhoehe, options));
		return doc;
	};
}
